import { Clock, CheckCircle, AlertCircle, PlayCircle, XCircle, User } from 'lucide-react';
import type { PatrolRound, PatrolStatus } from '@yawi/types';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';

interface PatrolRoundCardProps {
  round: PatrolRound;
  onClick?: () => void;
}

const statusConfig: Record<PatrolStatus, { color: string; bgColor: string; icon: typeof CheckCircle }> = {
  PENDING: { color: 'text-slate-600', bgColor: 'bg-slate-100', icon: Clock },
  IN_PROGRESS: { color: 'text-blue-600', bgColor: 'bg-blue-100', icon: PlayCircle },
  COMPLETED: { color: 'text-green-600', bgColor: 'bg-green-100', icon: CheckCircle },
  INCOMPLETE: { color: 'text-amber-600', bgColor: 'bg-amber-100', icon: AlertCircle },
};

export const PatrolRoundCard = ({ round, onClick }: PatrolRoundCardProps) => {
  const { t } = useI18n();
  const { formatDateTime, formatDuration } = useDateFormatter();
  const statusStyle = statusConfig[round.status];
  const StatusIcon = statusStyle.icon;

  const getDuration = () => {
    if (!round.completedAt) return null;
    const start = new Date(round.startedAt);
    const end = new Date(round.completedAt);
    const diffHours = (end.getTime() - start.getTime()) / 3600000;
    return formatDuration(diffHours);
  };

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${statusStyle.bgColor}`}>
            <StatusIcon className={`w-4 h-4 ${statusStyle.color}`} />
          </div>
          <div>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bgColor} ${statusStyle.color}`}>
              {t(`patrol.status.${round.status.toLowerCase()}`)}
            </span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">{formatDateTime(round.startedAt)}</p>
          {getDuration() && (
            <p className="text-xs text-slate-400">{getDuration()}</p>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-slate-600">{t('patrol.progress')}</span>
          <span className="font-semibold text-slate-800">
            {round.scannedCheckpoints}/{round.totalCheckpoints}
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              round.completionPercentage === 100
                ? 'bg-green-500'
                : round.completionPercentage >= 50
                ? 'bg-blue-500'
                : 'bg-amber-500'
            }`}
            style={{ width: `${round.completionPercentage}%` }}
          />
        </div>
        <p className="text-xs text-slate-500 text-right mt-1">
          {round.completionPercentage.toFixed(0)}%
        </p>
      </div>

      {/* Guard info */}
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <User className="w-3 h-3" />
        <span>{round.guardName}</span>
      </div>

      {/* Notes */}
      {round.notes && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <p className="text-xs text-slate-500 line-clamp-2">{round.notes}</p>
        </div>
      )}
    </div>
  );
};
